import LandingTemplate from "@/components/LandingTemplate";
import { MFCalculator } from "@/components/Calculators";

export default function StartInvesting() {
  return (
    <LandingTemplate
      audience="individual"
      seo={{
        path: "/start-investing",
        title: "Start Investing",
        description: "New to investing? A calm, plan-first way to begin — understand your goals, pick suitable products and start with confidence. Rao Investment Solutions.",
      }}
      breadcrumbs={[{ name: "Home", to: "/" }, { name: "Resources" }, { name: "Start Investing" }]}
      hero={{
        label: "Resource",
        title: "Start investing — without the guesswork.",
        intro: "Money sitting idle quietly loses value to inflation. Let's turn hesitation into a simple, suitable first step.",
        cta: "Book a free starter session",
      }}
      painPoints={[
        "I keep meaning to start, but I don't know where to begin.",
        "There are too many options — SIP, stocks, PMS — and no clear answer for me.",
        "I'm worried about picking the wrong thing and losing money.",
        "My savings are sitting in a bank account earning almost nothing.",
        "Everyone gives me tips, but no one looks at my full picture.",
      ]}
      whyMatters={{
        title: "The cost of waiting is invisible — until it isn't.",
        paras: [
          "Inflation erodes idle cash every year. Money that isn't working is quietly shrinking in real terms.",
          "Time in the market matters more than timing it. Starting earlier — even small — gives compounding room to work.",
          "A suitable first step, sized to your comfort, beats a perfect plan you never begin.",
        ],
      }}
      approach={{
        title: "A calm, four-stage way to begin",
        intro: "No jargon, no pressure — just a sensible sequence.",
        stages: [
          { title: "Understand", body: "We map your goals, income, obligations and comfort with risk. This shapes everything else." },
          { title: "Prioritise", body: "Emergency buffer and protection first, then growth. We sequence what to fund and when." },
          { title: "Select", body: "We match suitable products to each goal — often starting with a simple SIP." },
          { title: "Review", body: "We set a review rhythm so your plan adapts as life and markets change." },
        ],
      }}
      solutions={[
        { name: "Mutual Funds (SIP)", to: "/products/mf-pms-aif", desc: "Start small and automate. Goal-based portfolios via SIP are the most common first step." },
        { name: "Fixed Income", to: "/products/fixed-income-insurance", desc: "Add stability and income with bonds and fixed-income options suited to your horizon." },
        { name: "Protection first", to: "/products/fixed-income-insurance", desc: "Health and accident cover so an emergency doesn't derail your plan before it starts." },
      ]}
      calculator={<MFCalculator />}
      calculatorTitle="See what a simple SIP could grow into"
      howWeHelp={[
        { title: "Free starter call", body: "A relaxed conversation about your goals — no commitment." },
        { title: "Simple plan", body: "A one-page starting plan with a suitable first product." },
        { title: "Easy onboarding", body: "We handle KYC and setup so you can begin quickly." },
        { title: "Ongoing reviews", body: "Scheduled check-ins to keep you on track." },
      ]}
      whoFor={[
        "First-time investors who want a guided start",
        "Salaried professionals with idle savings",
        "Young families building a foundation",
        "Anyone tired of tips without a plan",
      ]}
      faq={[
        { q: "How much do I need to start?", a: "You can begin a SIP with a modest monthly amount. We'll suggest a figure that's comfortable and meaningful for your goals." },
        { q: "Is my money locked in?", a: "Most starter options like open-ended mutual funds are liquid. We'll flag any lock-ins clearly before you invest." },
        { q: "Will I lose money?", a: "All market-linked investments carry risk and can fall in value. We manage this with suitability, diversification and time — never with guarantees." },
      ]}
      finalCta={{ title: "Your first step is a conversation.", intro: "Book a free starter session and leave with a clear, simple next move." }}
    />
  );
}
